import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { User, Phone, Mail, Hash, ChevronRight, CheckCircle, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BR_STATES = [
  { uf: "AC", name: "Acre" }, { uf: "AL", name: "Alagoas" }, { uf: "AP", name: "Amapá" },
  { uf: "AM", name: "Amazonas" }, { uf: "BA", name: "Bahia" }, { uf: "CE", name: "Ceará" },
  { uf: "DF", name: "Distrito Federal" }, { uf: "ES", name: "Espírito Santo" }, { uf: "GO", name: "Goiás" },
  { uf: "MA", name: "Maranhão" }, { uf: "MT", name: "Mato Grosso" }, { uf: "MS", name: "Mato Grosso do Sul" },
  { uf: "MG", name: "Minas Gerais" }, { uf: "PA", name: "Pará" }, { uf: "PB", name: "Paraíba" },
  { uf: "PR", name: "Paraná" }, { uf: "PE", name: "Pernambuco" }, { uf: "PI", name: "Piauí" },
  { uf: "RJ", name: "Rio de Janeiro" }, { uf: "RN", name: "Rio Grande do Norte" },
  { uf: "RS", name: "Rio Grande do Sul" }, { uf: "RO", name: "Rondônia" }, { uf: "RR", name: "Roraima" },
  { uf: "SC", name: "Santa Catarina" }, { uf: "SP", name: "São Paulo" }, { uf: "SE", name: "Sergipe" },
  { uf: "TO", name: "Tocantins" },
];

const CATEGORIES = [
  { value: "enfermeiro", label: "Enfermeiro(a)", council: "COREN" },
  { value: "tecnico", label: "Técnico(a) de Enfermagem", council: "COREN" },
  { value: "acs", label: "Agente Comunitário de Saúde", council: "Matrícula" },
  { value: "medico", label: "Médico(a)", council: "CRM" },
  { value: "coordenador", label: "Gestor(a) de Unidade / Coordenador(a) APS", council: "Matrícula" },
  { value: "secretario", label: "Secretário(a) de Saúde", council: "Matrícula" },
  { value: "especialista", label: "Especialista de Referência", council: "CRM/COREN" },
];

export default function ProfileSetupPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    professional_category: "",
    council_number: "",
    phone: "",
    notification_email: user?.email || "",
    health_unit_id: "",
    state: "",
    municipality: "",
  });
  const [municipalities, setMunicipalities] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user?.profile_complete && user?.is_validated) navigate("/dashboard");
    if (user?.profile_complete && !user?.is_validated) navigate("/pending-approval");
  }, [user, navigate]);

  // Carrega municípios do IBGE ao selecionar o estado
  useEffect(() => {
    if (!form.state) { setMunicipalities([]); return; }
    setLoadingMunicipalities(true);
    setForm(p => ({ ...p, municipality: "" }));
    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${form.state}/municipios?orderBy=nome`)
      .then(r => r.json())
      .then(data => setMunicipalities(data.map(m => m.nome)))
      .catch(() => setMunicipalities([]))
      .finally(() => setLoadingMunicipalities(false));
  }, [form.state]);

  const selectedCategory = CATEGORIES.find(c => c.value === form.professional_category);
  const setField = (f, v) => { setForm(p => ({ ...p, [f]: v })); if (errors[f]) setErrors(p => ({ ...p, [f]: "" })); };

  // Step 1: Localização, Step 2: Dados Profissionais, Step 3: Contato
  const validateStep1 = () => {
    const errs = {};
    if (!form.state) errs.state = "Selecione seu estado";
    if (!form.municipality) errs.municipality = "Selecione seu município";
    return errs;
  };
  const validateStep2 = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Nome completo é obrigatório";
    if (!form.professional_category) errs.professional_category = "Selecione sua categoria profissional";
    return errs;
  };
  const validateStep3 = () => {
    const errs = {};
    if (!form.phone.trim()) errs.phone = "Telefone é obrigatório";
    if (!form.notification_email.trim()) errs.notification_email = "Email é obrigatório";
    return errs;
  };

  const handleNextStep = () => {
    const errs = step === 1 ? validateStep1() : validateStep2();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setStep(s => s + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateStep3();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      const resp = await axios.put(`${API}/users/profile`, form, { withCredentials: true });
      setUser(resp.data);
      navigate("/pending-approval");
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const TOTAL_STEPS = 3;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
            <User className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>Complete seu Cadastro</h1>
          <p className="text-muted-foreground text-sm mt-1">Olá, {user?.name?.split(" ")[0]}! Precisamos de algumas informações adicionais.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-2 animate-fade-in">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step >= s ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              {s < TOTAL_STEPS && <div className={`flex-1 h-0.5 mx-1 ${step > s ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mb-6 px-1">
          <span>Localização</span><span className="text-center">Profissional</span><span className="text-right">Contato</span>
        </div>

        {/* Step 1: Estado e Município */}
        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="bg-white rounded-2xl border border-border p-6 space-y-5 animate-fade-in">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-5 h-5 text-primary" />
              <p className="text-sm font-medium text-foreground">Onde você atua?</p>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Isso vincula você ao município correto e direciona sua aprovação ao gestor responsável.</p>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Estado (UF) *</label>
              <select value={form.state} onChange={e => setField("state", e.target.value)}
                className={`mt-1 w-full h-12 border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.state ? "border-destructive" : "border-border"}`}
                data-testid="state-select">
                <option value="">Selecione o estado</option>
                {BR_STATES.map(s => <option key={s.uf} value={s.uf}>{s.uf} — {s.name}</option>)}
              </select>
              {errors.state && <p className="text-xs text-destructive mt-1">{errors.state}</p>}
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Município *</label>
              {loadingMunicipalities ? (
                <div className="mt-1 h-12 border border-border rounded-lg flex items-center px-3 text-sm text-muted-foreground animate-pulse">Carregando municípios...</div>
              ) : (
                <select value={form.municipality} onChange={e => setField("municipality", e.target.value)}
                  disabled={!form.state}
                  className={`mt-1 w-full h-12 border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 ${errors.municipality ? "border-destructive" : "border-border"}`}
                  data-testid="municipality-select">
                  <option value="">{form.state ? "Selecione o município" : "Selecione o estado primeiro"}</option>
                  {municipalities.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
              {errors.municipality && <p className="text-xs text-destructive mt-1">{errors.municipality}</p>}
            </div>
            <button type="submit"
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all"
              data-testid="next-step-btn">
              Continuar <ChevronRight className="w-4 h-4" />
            </button>
          </form>
        )}

        {/* Step 2: Dados Profissionais */}
        {step === 2 && (
          <div className="bg-white rounded-2xl border border-border p-6 space-y-5 animate-fade-in">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome Completo *</label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="text" value={form.full_name} onChange={e => setField("full_name", e.target.value)}
                  placeholder="Seu nome completo"
                  className={`w-full h-12 border rounded-lg pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.full_name ? "border-destructive" : "border-border"}`}
                  data-testid="full-name-input" />
              </div>
              {errors.full_name && <p className="text-xs text-destructive mt-1">{errors.full_name}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Categoria Profissional *</label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">Selecione sua função no município</p>
              <div className="space-y-2">
                {CATEGORIES.map(cat => (
                  <label key={cat.value} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${form.professional_category === cat.value ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`} data-testid={`category-${cat.value}`}>
                    <input type="radio" value={cat.value} checked={form.professional_category === cat.value} onChange={() => setField("professional_category", cat.value)} className="accent-primary" />
                    <div><p className="text-sm font-medium text-foreground">{cat.label}</p><p className="text-xs text-muted-foreground">Registro: {cat.council}</p></div>
                  </label>
                ))}
              </div>
              {errors.professional_category && <p className="text-xs text-destructive mt-1">{errors.professional_category}</p>}
            </div>
            {selectedCategory && (
              <div className="animate-fade-in">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Número do {selectedCategory.council}</label>
                <div className="relative mt-1">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input type="text" value={form.council_number} onChange={e => setField("council_number", e.target.value)}
                    placeholder={`Número do ${selectedCategory.council}`}
                    className="w-full h-12 border border-border rounded-lg pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="council-number-input" />
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setStep(1)} className="flex-1 py-3.5 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all">Voltar</button>
              <button type="button" onClick={handleNextStep}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all"
                data-testid="next-step-btn-2">
                Continuar <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Contato */}
        {step === 3 && (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-border p-6 space-y-5 animate-fade-in">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Telefone *</label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="tel" value={form.phone} onChange={e => setField("phone", e.target.value)} placeholder="(00) 00000-0000"
                  className={`w-full h-12 border rounded-lg pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.phone ? "border-destructive" : "border-border"}`}
                  data-testid="phone-input" />
              </div>
              {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email para Alertas *</label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Você receberá notificações de pacientes críticos aqui</p>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input type="email" value={form.notification_email} onChange={e => setField("notification_email", e.target.value)}
                  className={`w-full h-12 border rounded-lg pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors.notification_email ? "border-destructive" : "border-border"}`}
                  data-testid="notification-email-input" />
              </div>
              {errors.notification_email && <p className="text-xs text-destructive mt-1">{errors.notification_email}</p>}
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setStep(2)} className="flex-1 py-3.5 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all">Voltar</button>
              <button type="submit" disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
                data-testid="submit-profile-btn">
                {saving ? "Enviando..." : "Enviar para Validação"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
